import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row, Button, Modal, ProgressBar } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { getGeoStorageLocation, getGeoStorageReport, getLogisticGeoStorageReport, getTotalTareInfo } from '../../../actions/geo';
import { getDateRange, validateCollections } from '../../../helpers/common';
import { Unit } from '../../../helpers/unit';
import { SubNavDivider } from '../../layout/submenu/SubNav';
import { StorageLocationForm } from './StorageLocationForm';
import { toDisplayDateTimeString2 } from '../../../helpers/moment-ex';
import { DISPLAY_DATE_FORMAT2, HARVESTING_PLAN_TYPE_ID, LOGISTIC_PLAN_TYPE_ID, PLANTING_PLAN_TYPE_ID } from '../../../constants';
import { getDateRangeInfo } from '../../../actions/analyze';
import { Icon } from '../../widgets';

export const StorageLocation = ({ geoRegionShapeId, shapeName, cropName, totalArea, settings, shapeId, isCardBody, isRequesting: propsRequesting }) => {
	const [show, setShow] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isRefresh, setRefresh] = useState(false);

	const weightShortName = useSelector(state => state.user.settings.weightShortName);
	const { isRequesting, requestingShapeIds, payload: storageLocations } = useSelector(state => state.geo.geoStorageLocations);
	const { isRequesting: reportRequesting, requestingShapeIds: requestingReportShapeIds, payload: storageReport } = useSelector(state => state.geo.geoStorageReport);
	const { custom: { from, to }, range, customRange } = useSelector(state => state.analyze.searchParams);
	const { farm_uuid: farmId, timezone_identifier: timezone } = useSelector(state => state.farm.currentFarm);
	const timezoneIdentifier = useSelector(state => state.farm.currentFarm.timezone_identifier);
	const { selectedPlanType: selectedPlanTypeId } = useSelector((state => state.plan));
	const totalTareCapacity = useSelector(state => state.geo.totalTareCapacity);
	const { payload: LogisticStorageReport, rangeWiseReport: LogisticStorageRangeWiseReport } = useSelector(state => state.geo.geoLogisticStorageReport);
	const logisticLoadList = useSelector(state => state.logistics.logisticsHistory.data.history);
	const seedLoadList = useSelector(state => state.plan.seedHistory.data.history);
	const dispatch = useDispatch();

	const unit = useMemo(() => Unit.getInstance(), []);

	const planTypeId = useMemo(() => selectedPlanTypeId === LOGISTIC_PLAN_TYPE_ID ? HARVESTING_PLAN_TYPE_ID : selectedPlanTypeId === PLANTING_PLAN_TYPE_ID ?
		LOGISTIC_PLAN_TYPE_ID : selectedPlanTypeId, [selectedPlanTypeId]);
	const subPlanTypeId = useMemo(() => selectedPlanTypeId === HARVESTING_PLAN_TYPE_ID ? HARVESTING_PLAN_TYPE_ID : null, [selectedPlanTypeId]);

	useEffect(() => {
		if (!geoRegionShapeId) return;

		const commonParams = {
			geo_region_shape_uuid: geoRegionShapeId,
			from_date: from,
			to_date: to,
			range_type: range,
			farm_uuid: farmId,
		};

		dispatch(getGeoStorageLocation({ geo_region_shape_uuid: geoRegionShapeId }));

		dispatch(getGeoStorageReport({
			...commonParams,
			plan_type_id: planTypeId,
			sub_plan_type_id: subPlanTypeId
		}));

		dispatch(getLogisticGeoStorageReport({
			...commonParams,
			plan_type_id: selectedPlanTypeId === PLANTING_PLAN_TYPE_ID ? PLANTING_PLAN_TYPE_ID : LOGISTIC_PLAN_TYPE_ID,
			sub_plan_type_id: subPlanTypeId
		}));
	}, [geoRegionShapeId, from, to, range, farmId, planTypeId, subPlanTypeId]);

	useEffect(() => {
		getSearchHistory();
		getSearchHistory("fullList");
	}, [propsRequesting, isRefresh])

	// useEffect(() => {
	// 	dispatch(getLogisticGeoStorageReport({
	// 		geo_region_shape_uuid: geoRegionShapeId,
	// 		from_date: from,
	// 		to_date: to,
	// 		range_type: "todate",
	// 		farm_uuid: farmId,
	// 		plan_type_id: LOGISTIC_PLAN_TYPE_ID,
	// 		sub_plan_type_id: subPlanTypeId
	// 	}));
	// }, [isRefresh, selectedPlanTypeId]);



	const shapeStorageLocations = useMemo(() => storageLocations[geoRegionShapeId] || null, [geoRegionShapeId, storageLocations]);
	const currentStorageId = shapeStorageLocations && shapeStorageLocations.length > 0 ? shapeStorageLocations[0].geo_storage_location_uuid : null;

	const getFilteredLoads = (list, key) =>
		validateCollections(list) ? list.filter(it => it[key] === currentStorageId) : [];

	const getTotalWeight = (list) =>
		validateCollections(list) ? list.reduce((acc, it) => acc + unit.toRoundOfWeight(it.load_capacity), 0) : 0;

	const storageOutLoads = getFilteredLoads(logisticLoadList, "source_offload_geo_storage_location_uuid");
	// const outLoadsCount = storageOutLoads.length;

	const storageWeightOut = getTotalWeight(storageOutLoads);
	const storageOutSeedLoads = getFilteredLoads(seedLoadList, "offload_geo_storage_location_uuid");
	const storageSeedWeightOut = getTotalWeight(storageOutSeedLoads);

	const totalOutWeight = storageWeightOut + storageSeedWeightOut;
	const storageInLoads = getFilteredLoads(logisticLoadList, "offload_geo_storage_location_uuid");
	const storageWeightIn = getTotalWeight(storageInLoads);

	// const inLoadsCount = validateCollections(storageInLoads) && storageInLoads.length;
	let shapeStorageReport = useMemo(() => storageReport[geoRegionShapeId] || null, [geoRegionShapeId, storageReport]);
	const shapeLogisticStorageReport = useMemo(() => LogisticStorageReport[geoRegionShapeId] || null, [geoRegionShapeId, LogisticStorageReport]);
	const shapeLogisticStorageRangeWiseReport = useMemo(() => LogisticStorageRangeWiseReport[geoRegionShapeId] || null, [geoRegionShapeId, LogisticStorageRangeWiseReport]);

	shapeStorageReport = selectedPlanTypeId === LOGISTIC_PLAN_TYPE_ID ? shapeLogisticStorageReport : shapeStorageReport;
	const { totalFilled, rangeFilled, total, totalLoads, selectedTotalLoads, startDate, endDate, startTime, tareWeightTotal } = useMemo(() => {

		if (!shapeStorageReport || Object.keys(shapeStorageReport).length === 0) return {};
		const filled = unit.toWeight(shapeStorageReport.summary.filledCapacity, 2);
		const tareWeightTotal = unit.toWeight(shapeStorageReport.summary.tareWeight, 2);
		const rangeFilled = unit.toWeight(shapeStorageReport.summary.selectedFilledCapacity, 2);
		const total = unit.toWeight(shapeStorageReport.summary.totalCapacity, 2);
		const totalLoads = shapeStorageReport.summary.totalLoads;
		const selectedTotalLoads = shapeStorageReport.summary.selectedTotalLoads;
		const startDate = shapeStorageReport.summary.load_start_date ? moment.utc(shapeStorageReport.summary.load_start_date).tz(timezoneIdentifier).format(DISPLAY_DATE_FORMAT2) : "";
		const endDate = shapeStorageReport.summary.load_end_date ? moment.utc(shapeStorageReport.summary.load_end_date).tz(timezoneIdentifier).format(DISPLAY_DATE_FORMAT2) : "";

		return {
			totalFilledPercent: percent(filled, total, unit),
			filledPercent: percent((filled - rangeFilled), total, unit),
			rangeFilledPercent: percent(rangeFilled, total, unit),
			totalFilled: filled,
			rangeFilled,
			total,
			totalLoads,
			selectedTotalLoads,
			startDate,
			endDate,
			startTime,
			tareWeightTotal
		};
	}, [shapeStorageReport]);

	const { totalLogisticLoads, logisticsTotalFilled, logisticRangeFilled } = useMemo(() => {
		if (!shapeLogisticStorageReport || Object.keys(shapeLogisticStorageReport).length === 0) return {};

		const totalLogisticLoads = shapeLogisticStorageReport.summary ? shapeLogisticStorageReport.summary.totalLoads : null;
		const logisticsTotalFilled = unit.toWeight(shapeLogisticStorageReport && shapeLogisticStorageReport.summary &&
			shapeLogisticStorageReport.summary.filledCapacity, 2);
		const logisticRangeFilled = unit.toWeight(shapeLogisticStorageRangeWiseReport && shapeLogisticStorageRangeWiseReport.summary ?
			shapeLogisticStorageRangeWiseReport.summary.selectedFilledCapacity : null, 2);

		return {
			totalLogisticLoads,
			logisticsTotalFilled,
			logisticRangeFilled
		};
	}, [shapeLogisticStorageReport]);

	const percentProgress = (totalFilled, logisticFilled, isScore = false) => {
		let percentage;
		const netFilled = totalFilled && totalFilled > logisticFilled ? (totalFilled - logisticFilled) : 0;
		if (isScore) return percent((netFilled), total, unit);
		const netRangeFilled = rangeFilled && rangeFilled > logisticRangeFilled ? (rangeFilled - logisticRangeFilled) : 0;
		if (logisticFilled) {
			percentage = percent((netFilled && netFilled > netRangeFilled ? netFilled - netRangeFilled : 0), total, unit);
		} else {
			percentage = percent((totalFilled && totalFilled > rangeFilled ? (totalFilled - rangeFilled) : 0), total, unit);
		}
		return percentage;
	}

	const rangePercentProgress = (rangeFilled, logisticRangeFilled) => {
		let percentage;
		const netRangeFilled = rangeFilled - logisticRangeFilled
		if (logisticRangeFilled) {
			percentage = percent((netRangeFilled), total, unit);
		} else {
			percentage = percent(rangeFilled, total, unit);
		}
		return percentage;
	}
	const toalFilledPercent = percentProgress(totalFilled, logisticsTotalFilled);
	const toalFilledPercentScore = percentProgress(totalFilled, logisticsTotalFilled, true);
	const toalRangeFilledPercent = rangePercentProgress(rangeFilled, logisticRangeFilled);

	const handleHide = () => setShow(false);
	const handleShow = () => setShow(true);

	const initialRequesting = !shapeStorageLocations || !shapeStorageReport;
	const isFetching = (isRequesting && requestingShapeIds.includes(geoRegionShapeId))
		|| (reportRequesting && requestingReportShapeIds.includes(geoRegionShapeId));
	const isStorageEmpty = (shapeStorageLocations || []).length === 0;

	const { dateRange, dateRangeType } = useMemo(() => {
		const { name } = getDateRange({ range, custom: { from, to }, customRange });

		const formateDate = date => {
			const tzDate = moment(date);
			const currentTzDate = moment();
			const isCurrentYear = (tzDate.year() === currentTzDate.year());

			return toDisplayDateTimeString2(tzDate, isCurrentYear);
		};
		const isSameDateRange = moment(formateDate(from)).isSame(formateDate(to));
		const dateRange = (name === "Absolute") ? `${formateDate(from)} ${!isSameDateRange ? `- ${formateDate(to)}` : ""}` : name;
		return { dateRange, dateRangeType: name };
	}, [timezone, from, to, range, customRange]);

	const isLoading = useMemo(() => {
		if (!totalTareCapacity.hasOwnProperty(shapeId) && (propsRequesting || isFetching)) return true;
		else return false;

	}, [propsRequesting, isFetching])

	const getSearchHistory = (type) => {
		let dateInfo = {};
		if (type === 'fullList') {
			let fromDate = moment().startOf('year').format('YYYY-MM-DD HH:mm:ss');
			let toDate = moment().endOf('days').format('YYYY-MM-DD HH:mm:ss');

			if (from && (moment.utc().year() !== moment(from).year())) {
				fromDate = moment(`${moment(from).year()}-01-01`).startOf('year').format('YYYY-MM-DD HH:mm:ss');
				toDate = to
			}

			dateInfo = {
				range: "custom",
				fromDate,
				toDate
			}
		}
		else {
			dateInfo = dispatch(getDateRangeInfo());
		}
		const loadParams = {
			farmId: farmId,
			filter: 'last',
			region_uuid: shapeId ? shapeId : null,
			planTypeId: selectedPlanTypeId,
			...dateInfo,
			shapeId
		};
		dispatch(getTotalTareInfo(loadParams, type));
	};

	const renderFilledValue = (fullValue, tareLoadValue) => {
		//const finalValue = fullValue - tareLoadValue;
		return unit.formatNumber(unit.toFixedOf(fullValue, 2))
	}
	const renderFilledValueWithTare = (fullValue, tareLoadValue) => {
		const finalValue = fullValue + tareLoadValue;
		return unit.formatNumber(unit.toFixedOf(finalValue, 2))
	}
	const renderNetWeight = (fullValue, tareLoadValue) => {
		const finalValue = fullValue - tareLoadValue;
		return unit.formatNumber(unit.toFixedOf(finalValue, 2))
	}

	const tareCapacity = (tareCapacity, value) => {
		return tareCapacity && tareCapacity[value] ? tareCapacity[value] : 0
	}

	const isTareIncludedLoad = unit.toWeight(tareCapacity(totalTareCapacity[shapeId], "totalYearTareTotal"), 2);
	let totalFilledWeight;
	if (isTareIncludedLoad) {
		totalFilledWeight = renderFilledValue(totalFilled, (unit.toWeight(tareCapacity(totalTareCapacity[shapeId], "totalYearTareTotal"))));
	} else {
		totalFilledWeight = renderFilledValueWithTare(totalFilled, tareWeightTotal);
	}
	let rangeTotalFilledWeight;
	if (isTareIncludedLoad) {
		rangeTotalFilledWeight = renderFilledValue(rangeFilled, unit.toWeight(tareCapacity(totalTareCapacity[shapeId], "selectedDateTareTotal"), 2));
	} else {
		rangeTotalFilledWeight = renderFilledValueWithTare(rangeFilled, tareWeightTotal);
	}
	// const remainingWeight = parseInt(totalFilledWeight ? totalFilledWeight.replace(/,/g, "") : 0, 10) - logisticsTotalFilled;
	const remainingWeight = Number(totalFilledWeight ? renderNetWeight(totalFilled, tareWeightTotal ? 0 : unit.toWeight(tareCapacity(totalTareCapacity[shapeId], "totalYearTareTotal"), 2)).replace(/,/g, "") : 0, 10) - logisticsTotalFilled;
	return (
		<div className={`storage-location ${isStorageEmpty ? "" : "storagelist-exist"}`}>
			<div className='shape-popup__refresh-hldr d-none'>
				<i
					title="Refresh"
					className="fa fa-refresh btn-refresh"
					aria-hidden="true"
					onClick={() => setRefresh(!isRefresh)}
				/>
			</div>
			{cropName && cropName.trim().length > 0 ?
				<Row className="m-0 row-mb">
					<Col lg={4} sm={4} md={4} className="p-0"><b>Crop</b></Col>
					<Col lg={8} sm={8} md={8} className="p-0">
						<span className="mr-2 font-weight-bold">:</span>
						{cropName}
					</Col>
				</Row> : null}
			{totalArea !== null && <Row className="m-0 row-mb">
				<Col lg={4} sm={4} md={4} className="p-0"><b>{settings.areaShortName}</b></Col>
				<Col lg={8} sm={8} md={8} className="p-0">
					<span className="mr-2 font-weight-bold">:</span>
					{totalArea}
				</Col>
			</Row>}

			{isLoading ? <div className="d-flex justify-content-center my-2">Loading...</div> : null}
			{!isLoading && (
				((shapeStorageReport && shapeStorageReport.storageList) || []).length > 0 && (
					<>
						<h5 className='mt-0 offload_field-header'>Storage Summary</h5>
						<div className={`storage-list field_content_list ${isStorageEmpty ? 'list-empty' : ''}`}>
							{!isStorageEmpty && !isCardBody ? <div className="d-flex mt-4 mb-4">
								<div className="flex-grow-1">
									<ProgressBar>
										<ProgressBar style={{ backgroundColor: "#ffca7d" }} now={toalFilledPercent} key={2} />
										<ProgressBar style={{ backgroundColor: "#f7a32b" }} now={toalRangeFilledPercent} key={1} />
										<ProgressBar style={{ backgroundColor: "#eee" }} now={100 - (toalFilledPercent + toalRangeFilledPercent)} key={3} />
									</ProgressBar>
								</div>
								<div className={`ml-2 font-weight-bold ${toalFilledPercentScore > 100 ? 'color-red' : 'color-green'}`}>{`${toalFilledPercentScore}%`}</div>
							</div> : null}
							<ul>
								<li>
									{!isStorageEmpty ? <div className="d-flex align-items-center">
										<div className="p-0 font-weight-bold field_ordered_header">Storage Capacity</div>
										<div className="p-0 field_ordered_data">
											<span className="mx-2 font-weight-bold">:</span>
											{unit.formatNumber(total)}<sub>{weightShortName}</sub></div>
									</div> : null}
								</li>

								<li>
									<div className="d-flex align-items-center">
										<div className="p-0 font-weight-bold field_ordered_header">Total Filled</div>
										<div className="p-0 field_ordered_data">
											<span className="mx-2 font-weight-bold">:</span>
											{totalFilledWeight}<sub>{weightShortName}</sub>
											<span className="mx-2">|</span>
											<span>{totalLoads /* - tareCapacity(totalTareCapacity[shapeId], "totalYearLoadCount") */}<sub>load(s)</sub></span>
											<span style={{ color: "#898B8D", fontSize: "smaller" }}>{startDate ? ` since ${startDate}` : ""}</span>
										</div>
									</div>
								</li>
								{/* <li>
                                    <div className="d-flex align-items-center">
                                        <div className="p-0 font-weight-bold field_ordered_header">Total Tare</div>
                                        <div className="p-0 field_ordered_data">
                                            <span className="mx-2 font-weight-bold">:</span>
                                            {unit.toWeight(tareCapacity(totalTareCapacity[shapeId], "totalYearTareTotal"), 2)}<sub>{weightShortName}</sub>
                                            <span className="mx-2">|</span>
                                            <span>{tareCapacity(totalTareCapacity[shapeId], "totalYearLoadCount")}<sub>load(s)</sub></span>
                                            <span style={{ color: "#898B8D", fontSize: "smaller" }}>{startDate ? ` since ${startDate}` : ""}</span>
                                        </div>
                                    </div>
                                </li> */}
								<li>
									<div className="d-flex align-items-center">
										<div className="p-0 font-weight-bold field_ordered_header">Total Net Weight</div>
										<div className="p-0 field_ordered_data">
											<span className="mx-2 font-weight-bold">:</span>
											{renderNetWeight(totalFilled, tareWeightTotal ? 0 : unit.toWeight(tareCapacity(totalTareCapacity[shapeId], "totalYearTareTotal"), 2))}<sub>{weightShortName}</sub>
											<span style={{ color: "#898B8D", fontSize: "smaller" }}>{startDate ? ` since ${startDate}` : ""}</span>
										</div >
									</div >
								</li >
								<li>
									<div className="d-flex align-items-center">
										<div className="p-0 font-weight-bold field_ordered_header">Filled {dateRangeType === "Absolute" ? 'on' : ''} {dateRange}</div>
										<div className="p-0 field_ordered_data">
											<span className="mx-2 font-weight-bold">:</span>
											{rangeTotalFilledWeight}<sub>{weightShortName}</sub>
											<span className="mx-2">|</span>
											<span>{selectedTotalLoads /* - tareCapacity(totalTareCapacity[shapeId], "totalDateWiseLoadCount") */}<sub>load(s)</sub></span>
											<span style={{ color: "#898B8D", fontSize: "smaller" }}>{endDate ? ` until ${endDate}` : ""}</span>
										</div>
									</div>
								</li >
								<li>
									<div className="d-flex align-items-center">
										<div className="p-0 font-weight-bold field_ordered_header">Net Weight {dateRangeType === "Absolute" ? 'on' : ''} {dateRange}</div>
										<div className="p-0 field_ordered_data">
											<span className="mx-2 font-weight-bold">:</span>
											{!isTareIncludedLoad ? rangeFilled :
												renderNetWeight(rangeFilled, unit.toWeight(tareCapacity(totalTareCapacity[shapeId], "selectedDateTareTotal"), 2))}<sub>{weightShortName}</sub>
											<span style={{ color: "#898B8D", fontSize: "smaller" }}>{endDate ? ` until ${endDate}` : ""}</span>
										</div >
									</div >
								</li >
								{
									!(selectedPlanTypeId === LOGISTIC_PLAN_TYPE_ID) ? <>
										<li>
											<div className="d-flex align-items-center">
												<div className="p-0 font-weight-bold field_ordered_header">Withdrawn</div>
												<div className="p-0 field_ordered_data">
													<span className="mx-2 font-weight-bold">:</span>
													{unit.formatNumber(logisticsTotalFilled)}<sub>{weightShortName}</sub>
													<span className="mx-2">|</span>
													<span>{totalLogisticLoads}<sub>load(s)</sub></span>
													<span style={{ color: "#898B8D", fontSize: "smaller" }}>{startDate ? ` since ${startDate}` : ""}</span>
												</div>
											</div>
										</li>
										<li>
											<div className="d-flex align-items-center">
												<div className="p-0 font-weight-bold field_ordered_header">Remaining</div>
												<div className="p-0 field_ordered_data">
													<span className="mx-2 font-weight-bold">:</span>
													{remainingWeight > 0 ? unit.formatNumber(unit.toFixedOf(remainingWeight, 2)) : 0}<sub>{weightShortName}</sub>
													{startDate && <span className="mx-2">|</span>}
													<span style={{ color: "#898B8D", fontSize: "smaller" }}>{startDate ? ` since ${startDate}` : ""}</span>
												</div>
											</div>
										</li>
									</> : null
								}
								{
									selectedPlanTypeId === LOGISTIC_PLAN_TYPE_ID ?
										<>
											<li>
												<div className="d-flex align-items-center">
													<div className="p-0 font-weight-bold field_ordered_header">Weight In</div>
													<div className="p-0 field_ordered_data d-flex align-items-center">
														<span className="mx-2 font-weight-bold">:</span>
														<div className='d-flex justify-content-center align-items-center dot_arrow-hldr mr-2'>
															<Icon title=""
																ariaHidden="true"
																className="fa fa-arrow-left mr-1"></Icon>
															<div className="dot_arrow_txt"></div>
															<div className="dot_arrow_txt"></div>
														</div>
														{storageWeightIn}<sub>{weightShortName}</sub>
														<span className="mx-2">|</span>
														{/* <span>{inLoadsCount}<sub>load(s)</sub></span> */}
														<span style={{ color: "#898B8D", fontSize: "smaller" }} className="ml-1">{startDate ? ` since ${startDate}` : ""}</span>
													</div>
												</div>
											</li>
											<li>
												<div className="d-flex align-items-center">
													<div className="p-0 font-weight-bold field_ordered_header">Weight Out</div>
													<div className="p-0 field_ordered_data d-flex align-items-center">
														<span className="mx-2 font-weight-bold">:</span>
														<div className='d-flex justify-content-center align-items-center dot_arrow-hldr mr-2'>
															<div className="dot_arrow_txt"></div>
															<div className="dot_arrow_txt"></div>
															<Icon title=""
																ariaHidden="true"
																className="fa fa-arrow-right mr-1"></Icon>
														</div>
														{totalOutWeight}<sub>{weightShortName}</sub>
														<span className="mx-2">|</span>
														{/* <span>{outLoadsCount}<sub>load(s)</sub></span> */}
														<span style={{ color: "#898B8D", fontSize: "smaller" }} className="ml-1">{startDate ? ` since ${startDate}` : ""}</span>
													</div>
												</div>
											</li>
										</> : null
								}
								{/*  <li>
                                    <div className="d-flex align-items-center">
                                        <div className="p-0 font-weight-bold field_ordered_header">Tare {dateRangeType === "Absolute" ? 'on' : ''} {dateRange}</div>
                                        <div className="p-0 field_ordered_data">
                                            <span className="mx-2 font-weight-bold">:</span>
                                            {unit.toWeight(tareCapacity(totalTareCapacity[shapeId], "selectedDateTareTotal"), 2)}<sub>{weightShortName}</sub>
                                            <span className="mx-2">|</span>
                                            <span>{tareCapacity(totalTareCapacity[shapeId], "totalDateWiseLoadCount")}<sub>load(s)</sub></span>
                                            <span style={{ color: "#898B8D", fontSize: "smaller" }}>{endDate ? ` until ${endDate}` : ""}</span>
                                        </div>
                                    </div>
                                </li> */}

							</ul >
							<SubNavDivider />
							<div className="d-flex field_content_list storage-list__hldr flex-column">
								{(shapeStorageReport.storageList || []).map(storage =>
									<StorageRow
										totalTare={unit.toWeight(tareCapacity(totalTareCapacity[shapeId], "totalYearTareTotal"), 2)}
										totalFilled={totalFilled}
										key={storage.geoStorageLocationId}
										storage={storage}
										unit={unit}
										isTareIncludedLoad={isTareIncludedLoad}
										shapeStorageLocations={shapeStorageLocations}
										logisticRangeWiseReport={shapeLogisticStorageRangeWiseReport}
										LogisticStorageReport={shapeLogisticStorageReport}
										loadStartDate={startDate}
									/>
								)}
							</div>
						</div >
					</>
				)
			)}

			{
				!initialRequesting && <Button
					size="sm"
					className="pl-0 link-btn float-right"
					variant="link"
					disabled={!shapeStorageReport}
					onClick={handleShow}>Add/Edit Storage and Bays</Button>
			}

			<Modal
				className="storage-location-form"
				backdrop="static"
				show={show}
				onHide={handleHide}>
				<StorageLocationForm
					onHide={handleHide}
					shapeName={shapeName}
					geoRegionShapeId={geoRegionShapeId}
					shapeStorageLocations={shapeStorageLocations}
					isSaving={isSaving}
					setIsSaving={setIsSaving} />
			</Modal>
		</div >
	);
};

const StorageRow = ({
	storage,
	shapeStorageLocations,
	unit,
	totalFilled,
	totalTare,
	isTareIncludedLoad,
	logisticRangeWiseReport,
	LogisticStorageReport,
	loadStartDate
}) => {
	const { geoStorageLocationId, filledCapacity, selectedFilledCapacity, totalLoad } = storage;

	const weightShortName = useSelector(state => state.user.settings.weightShortName);
	const { selectedPlanType: selectedPlanTypeId } = useSelector((state => state.plan));

	const logisticRangeFilled = logisticRangeWiseReport && logisticRangeWiseReport.storageList.length > 0 ?
		logisticRangeWiseReport.storageList.find(it => it.geoStorageLocationId === geoStorageLocationId) : null;

	const selectedLogisticRangeCapacity = logisticRangeFilled && logisticRangeFilled.selectedFilledCapacity ?
		logisticRangeFilled.selectedFilledCapacity : null;

	const LogisticFilled = LogisticStorageReport && LogisticStorageReport.storageList.length > 0 ?
		LogisticStorageReport.storageList.find(it => it.geoStorageLocationId === geoStorageLocationId) : null;

	const LogisticCapacity = LogisticFilled && LogisticFilled.filledCapacity ?
		LogisticFilled.filledCapacity : null;

	const LogisticTotalLoads = LogisticFilled && LogisticFilled.totalLoad ?
		LogisticFilled.totalLoad : 0;

	const storageInfo = shapeStorageLocations.find(e => e.geo_storage_location_uuid === geoStorageLocationId) || {};

	const { storage_location_capacity, storage_location_name } = storageInfo;

	const {
		storageCapacity,
		totalCapacity,
		totalCapacityNonReadable,
		filledPercentage,
		rangeFilledPercentage
	} = useMemo(() => {

		const storageCapacity = unit.toWeight((filledCapacity || 0), 2);

		const totalCapacity = unit.toWeight(storage_location_capacity || 0, 2);

		const rangeFilledCapacity = unit.toWeight((selectedFilledCapacity) || 0, 2);

		const logisticRangeFilledCapacity = unit.toWeight(selectedLogisticRangeCapacity || 0, 2);

		const percentProgress = (totalFilled, logisticFilled, isScore = false) => {
			let percentage;
			const netFilled = totalFilled && (totalFilled > logisticFilled) ? (totalFilled - logisticFilled) : 0;
			if (isScore) return percent((netFilled), totalCapacity, unit);
			const netRangeFilled = rangeFilledCapacity && (rangeFilledCapacity > logisticRangeFilledCapacity) ? (rangeFilledCapacity - logisticRangeFilledCapacity) : 0;
			if (logisticFilled) {
				percentage = percent((netFilled && (netFilled > netRangeFilled) ? (netFilled - netRangeFilled) : 0), totalCapacity, unit);
			} else {
				percentage = percent((totalFilled && (totalFilled > rangeFilledCapacity) ? (totalFilled - rangeFilledCapacity) : 0), totalCapacity, unit);
			}
			return percentage;
		}

		const rangePercentProgress = (rangeFilled, logisticRangeFilled) => {
			let percentage;
			const netRangeFilled = rangeFilled - logisticRangeFilled
			if (logisticRangeFilled) {
				percentage = percent((netRangeFilled), totalCapacity, unit);
			} else {
				percentage = percent(rangeFilled, totalCapacity, unit);
			}
			return percentage;
		}

		return {
			storageCapacity: storageCapacity,
			totalCapacity: unit.formatNumber(totalCapacity),
			totalCapacityNonReadable: Number(totalCapacity),
			filledPercentage: percentProgress(storageCapacity, unit.toWeight((LogisticCapacity || 0), 2)),
			rangeFilledPercentage: rangePercentProgress(rangeFilledCapacity, logisticRangeFilledCapacity)
		};

	}, [storage_location_capacity, filledCapacity, selectedLogisticRangeCapacity]);

	const { netCapacityReadable, netCapacity } = useMemo(() => {
		if (!storageCapacity || Number(totalLoad) === 0) return 0;
		const tarePercentage = !totalTare || !totalFilled ? 0 : (totalTare / totalFilled);
		const tareCapacity = storageCapacity * tarePercentage;
		return {
			netCapacityReadable: unit.formatNumber(unit.toFixedOf((!isTareIncludedLoad ? storageCapacity : storageCapacity - tareCapacity), 2)),
			netCapacity: unit.toFixedOf((storageCapacity - tareCapacity), 2)

		}
	}, [storageCapacity, totalFilled, totalTare, filledCapacity]);


	const percentageStorage = useMemo(() => {
		return percent(Number(netCapacity - unit.toWeight(LogisticCapacity)), Number(totalCapacityNonReadable), unit)
	}, [netCapacity, totalCapacityNonReadable, LogisticCapacity])

	const nonLogisticRemaining = netCapacity ? unit.formatNumber(unit.toFixedOf(netCapacity - unit.toWeight(LogisticCapacity), 2)) : 0;
	return (
		<div className='mb-3'>
			<h6 className="storage-title">{storage_location_name || 'Unknown'}</h6>
			{storage_location_name ?
				<div className='d-flex pr-3'>
					<ProgressBar className='storage-popup__progress-hldr w-100'>
						<ProgressBar style={{ backgroundColor: "#ffca7d" }} now={filledPercentage} key={2} />
						<ProgressBar style={{ backgroundColor: "#f7a32b" }} now={rangeFilledPercentage} key={1} />
						<ProgressBar style={{ backgroundColor: "#eee" }} now={100 - (filledPercentage + rangeFilledPercentage)} key={3} />
					</ProgressBar>
					<span className={`font-weight-bold ml-2 ${percentageStorage > 100 ? 'color-red' : 'color-green'}`}>{`${percentageStorage > 0 ? percentageStorage : 0}%`}</span>
				</div>
				: null}
			<ul className='m-0'>
				<li>
					<div className="d-flex fieldMap_lists flex-column">
						<div className='d-flex mt-3'>
							<div className={`storage-item-header font-weight-bold field_ordered_header ${!storage_location_name ? 'color-red' : ''}`}>{storage_location_name || 'Unknown'}
							</div>
							<div className="storage-item field_ordered_data overflow-unset d-flex">
								{storage_location_name ? (
									<>
										<span className="mx-2 font-weight-bold">:</span>

										<span><b>{netCapacityReadable}</b> {netCapacityReadable ? '/' : ''} {totalCapacity}
											<sub>{weightShortName}</sub>
											<span className="mx-2">|</span>
											{totalLoad}<sub>load(s)</sub>
										</span>
									</>) : (
									<>
										<span className="mx-2 font-weight-bold">:</span>
										<span>{netCapacityReadable && unit.formatNumber(netCapacityReadable)}
											<sub>{weightShortName}</sub>
											<span className="mx-2">|</span>
											{totalLoad}<sub>load(s)</sub>
										</span>
									</>
								)}
							</div>
						</div>
					</div>
				</li>
				{
					!(selectedPlanTypeId === LOGISTIC_PLAN_TYPE_ID) && LogisticTotalLoads ?
						<>
							<li>
								<div className="d-flex align-items-center">
									<div className="p-0 font-weight-bold field_ordered_header">Withdrawn</div>
									<div className="p-0 field_ordered_data">
										<span className="mx-2 font-weight-bold">:</span>
										{unit.toRoundOfWeight((LogisticCapacity || 0), true)}<sub>{weightShortName}</sub>
										<span className="mx-2">|</span>
										<span>{LogisticTotalLoads}<sub>load(s)</sub></span>
										<span style={{ color: "#898B8D", fontSize: "smaller" }}>{loadStartDate ? ` since ${loadStartDate}` : ""}</span>
									</div>
								</div>
							</li>
							<li>
								<div className="d-flex align-items-center">
									<div className="p-0 font-weight-bold field_ordered_header">Remaining</div>
									<div className="p-0 field_ordered_data">
										<span className="mx-2 font-weight-bold">:</span>
										{nonLogisticRemaining}<sub>{weightShortName}</sub>
										{loadStartDate && <span className="mx-2">|</span>}
										<span style={{ color: "#898B8D", fontSize: "smaller" }}>{loadStartDate ? ` since ${loadStartDate}` : ""}</span>
									</div>
								</div>
							</li>
						</> : null
				}

			</ul>
		</div>
	);
};

const percent = (a, b, unit) => (a === 0 || b === 0) ? 0 : unit.toFixedOf((a / b) * 100, 2);
